import _ from "lodash";
import ACLStore from "lib/aclStore";
import {
  getStyleStrippedText,
  getPYPElementSetFromNodes,
  generateNestedSelectedData,
} from "Utils";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getUnitPlanFieldFromCache } from "IBPlanner/modules/IBGraphqlHelpers";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getUniquelyCombinedGradesDetails } from "UnitPlans/modules/UnitPlanGraphqlHelpers";
import { ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION } from "Constants/permissionConstants";
import {
  CURRICULUM_TYPE_MYP,
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
} from "Constants/stringConstants";
import { getLearningCourseVariables } from "Teacher/routes/LearningCourse/modules/LearningCourseUtils";
import { getLearningCourseDetailsFromCache } from "Teacher/routes/LearningCourse/modules/LearningCourseGraphqlHelpers";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";

const getLearningCourseSubjects = ({ learningCourseId, academicYearId }) => {
  const learningCourseVariables = getLearningCourseVariables({
    learningCourseId,
    academicYearId,
  });
  const learningCourse = getLearningCourseDetailsFromCache(
    learningCourseVariables
  );
  return _.get(learningCourse, "subjects", []);
};

const getUnitPlanFieldResponseFilter = () => {
  const userInfo = getUserInfo();
  const userType = _.get(userInfo, "user_type");

  let responseFilter;

  if (userType === "student") {
    responseFilter = {
      studentIds: [_.get(userInfo, "id")],
    };
  }

  if (userType === "parent") {
    responseFilter = {
      studentIds: [_.get(userInfo, "childID")],
    };
  }

  return responseFilter;
};

const pypElementSetMapping = {
  lp: "learnerProfiles",
  action: "actions",
  concepts: "concepts",
  theme: "themes",
  atls: "atls",
  benchmarks: "benchmarks",
  subjects: "subjects",
  relatedConcepts: "relatedConcepts",
  subjectGroup: "subjectGroups",
  learningStandardUBD: "learningStandardUBD",
  atlsUBD: "atlsUBD",
  atlsMYP: "atlsMYP",
  learningStandardMYP: "learningStandardMYP",
  pypNestedElement: "nodes",
};

export const extractContentFromHTML = ({ htmlString, space = true }) => {
  if (!_.isEmpty(htmlString)) {
    const span = document.createElement("span");
    span.innerHTML = htmlString;

    if (space) {
      const children = span.querySelectorAll("*");
      const lengthOfChildren = _.get(children, "length", 0);

      for (let i = 0; i < lengthOfChildren; i++) {
        if (children[i].tagName === "IMG") {
          const altText = children[i].getAttribute("alt") || "image";
          const textNode = document.createTextNode(altText + " ");
          span.appendChild(textNode);
        } else if (children[i].textContent) {
          children[i].textContent += " ";
        } else {
          children[i].innerText += " ";
        }
      }
    }

    return [span.textContent || span.innerText].toString().replace(/ +/g, " ");
  } else {
    return "";
  }
};

export const getPropertyValue = ({ propertyPath, fieldObj }) => {
  return _.get(fieldObj, `${propertyPath}.value`);
};

export const getRichTextValue = ({
  value,
  shouldStripRichTextStyles = false,
}) => {
  return shouldStripRichTextStyles
    ? getStyleStrippedText({
        text: value,
        type: "RICH_TEXT_EDITOR",
      })
    : value;
};

const _getEmptyRichTextValueStatus = ({ value }) => {
  if (_.isNumber(value)) {
    return false;
  }
  if (_.isString(value)) {
    return _.isEmpty(_.trim(extractContentFromHTML({ htmlString: value })));
  }
  return _.isEmpty(value);
};

export const getInputNumberListValues = ({ value }) => {
  return _.reduce(
    value,
    (result, val) => {
      const value = _.get(val, "value", "");
      if (_.trim(value) !== "") {
        result.push(value);
      }
      return result;
    },
    []
  );
};

const _getEmptyInputNumberListValueStatus = ({ value }) => {
  return _.isEmpty(getInputNumberListValues({ value }));
};

const _getEmptyIBReflectionValueStatus = ({ valueObj }) => {
  const responses = _.get(valueObj, "responses.edges", []);
  return _.isEmpty(responses);
};

export const getChecklistValue = ({ valueObj }) => {
  const resolvedValue = _.get(valueObj, "resolvedMinimalTree.nodes", []);
  return _.map(resolvedValue, node => _.get(node, "label", ""));
};

const _getEmptyChecklistValueStatus = ({ valueObj }) => {
  return _.isEmpty(getChecklistValue({ valueObj }));
};

export const getNestedChecklistValue = ({ valueObj }) => {
  return _.get(valueObj, "resolvedMinimalTree.nodes", []);
};

const _getEmptyNestedChecklistValueStatus = ({ valueObj }) => {
  return _.isEmpty(getNestedChecklistValue({ valueObj }));
};

export const getEmptyFieldValueStatus = ({ fieldObj, data, fieldKey }) => {
  const allFields = _.unionBy(
    _.get(data, "allFields", []),
    _.get(data, "fields", []),
    "id"
  );
  const fieldValue = _.find(allFields, fieldValue => {
    return _.isEqual(_.get(fieldValue, "uid"), fieldKey);
  });
  const value = _.get(fieldValue, "value");

  const uiType = getPropertyValue({ propertyPath: "uiType", fieldObj });

  switch (uiType) {
    case "RichText": {
      return _getEmptyRichTextValueStatus({ value });
    }
    case "InputNumberList": {
      return _getEmptyInputNumberListValueStatus({ value });
    }
    case "IBReflection": {
      return _getEmptyIBReflectionValueStatus({ valueObj: fieldValue });
    }
    case "CHECKLIST": {
      return _getEmptyChecklistValueStatus({ valueObj: fieldValue });
    }
    case "NESTED_CHECKLIST": {
      return _getEmptyNestedChecklistValueStatus({ valueObj: fieldValue });
    }
    default: {
      return _.isEmpty(value);
    }
  }
};

// {normal property: bilingual property}
export const BILINGUAL_TRANSLATION_PROPERTY_MAPPING = {
  label: "bilingualLabel",
  subtext: "bilingualSubtext",
  promptsLabel: "bilingualPromptsLabel",
  placeholder: "bilingualPlaceholder",
};

export const isFieldPropertyPresent = ({ fieldObj, propertyPath }) => {
  return _.has(fieldObj, propertyPath);
};

export const getPlannerElementSetPropertyValue = ({
  plannerElementSetConfig,
  propertyName,
}) => {
  const propertyObj = _.find(
    plannerElementSetConfig,
    ({ config }) => config === propertyName
  );

  return _.get(propertyObj, "value");
};

export const getFieldEmptyText = ({ plannerElementSet, mode, t }) => {
  switch (mode) {
    case "view": {
      return "-";
    }
    case "preview":
    case "edit": {
      const plannerElementSetLabel = _.get(plannerElementSet, "label", "");

      return t("unitPlan:no_data_defined_with_label", {
        label: plannerElementSetLabel,
      });
    }
  }
};

export const getPlannerFieldOptions = ({
  grades,
  fieldObj,
  fieldKey,
  unitPlanFields,
  plannerElementNodeSets,
  defaultOptions,
  readOnly,
  plannerElements,
  plannerElementConfigType,
  t,
  objectiveLevelNodes,
  genericTags,
  courseId,
  curriculumType,
  field_listV2,
  subjectRecommendedPhaseMap,
  isCourseOverViewAndViewMode = false,
  shouldGroupObjectivesBySubject = false,
  organizationId,
}) => {
  const uiType = getPropertyValue({
    fieldObj,
    propertyPath: "uiType",
  });

  const plannerElementSetId = getPropertyValue({
    fieldObj,
    propertyPath: "plannerElementSetId",
  });

  const resolvedMinimalTree = _.get(
    _.find(unitPlanFields, { uid: fieldKey }),
    "resolvedMinimalTree",
    {}
  );
  switch (uiType) {
    case "NESTED_CHECKLIST": {
      const plannerElementNodeSet = _.find(plannerElementNodeSets, nodeSet => {
        const nodeSetPlannerElementSetId = _.get(
          nodeSet,
          "plannerElementSet.id"
        );

        return plannerElementSetId === nodeSetPlannerElementSetId;
      });

      const plannerElementNodeSetId = _.get(plannerElementNodeSet, "id");
      const configs = _.get(
        plannerElementNodeSet,
        "plannerElementSet.config",
        []
      );
      const plannerElementNodeSetData = {
        id: plannerElementNodeSetId,
        grades,
        configs,
      };
      return {
        grades,
        resolvedMinimalTree,
        plannerElementSetId,
        plannerElementNodeSetData,
      };
    }
    case "IBRelatedConceptsV2": {
      const selectedSubject = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "value",
        ""
      );

      const selectedConcepts = _.get(
        _.find(unitPlanFields, { uid: "concepts" }),
        "value",
        ""
      );

      const groupedOptionsFields = _.get(
        defaultOptions,
        "groupedOptionsFields",
        ["subjects", "concepts"]
      );
      const plannerElementNodeSetId = _.get(
        _.find(plannerElementNodeSets, {
          type: "PYP_RELATED_CONCEPT",
        }),
        "id"
      );
      return {
        plannerElementNodeSetId,
        subjects: selectedSubject ? selectedSubject : [],
        concepts: selectedConcepts ? selectedConcepts : [],
        groupedOptionsFields: groupedOptionsFields ? groupedOptionsFields : [],
      };
    }
    default: {
      switch (fieldKey) {
        case "theme":
        case "concepts":
        case "action":
        case "lp": {
          return getElementOptions({
            fieldKey,
            readOnly,
            plannerElements,
            plannerElementNodeSets,
            plannerElementConfigType,
            unitPlanFields,
            fieldObj,
          });
        }
        case "benchmarks":
          return getBenchmarksOptions({
            unitPlanFields,
            grades,
          });
        case "teacherQues":
          return getTeacherQuestionsOptions({ unitPlanFields });
        case "focusAreas":
          return getFocusAreaOptions({
            unitPlanFields,
            readOnly,
            plannerElements,
            plannerElementNodeSets,
            plannerElementConfigType: getPropertyValue({
              fieldObj,
              propertyPath: "plannerElementObj",
            }),
            field_listV2,
          });
        case "atls":
          return getAtlsOptions({
            grades,
            plannerElementNodeSets,
          });
        case "conceptsQuestion":
          return getElementQuesOptions({
            organizationId,
            fieldObj,
            unitPlanFields,
            uid: "concepts",
          });
        case "actionQuestion":
          return getElementQuesOptions({
            organizationId,
            fieldObj,
            unitPlanFields,
            uid: "action",
          });
        default: {
          if (!_.isEmpty(plannerElementSetId)) {
            return getPlannerElementOptions({
              t,
              grades,
              fieldObj,
              plannerElements,
              plannerElementNodeSets,
              unitPlanFields,
              objectiveLevelNodes,
              fieldKey,
              genericTags,
              fieldList: field_listV2,
              courseId,
              subjectRecommendedPhaseMap,
              curriculumType,
              isCourseOverViewAndViewMode,
              shouldGroupObjectivesBySubject,
            });
          }
          return defaultOptions ? defaultOptions : [];
        }
      }
    }
  }
};

export const getSubjectsFromDataSetNodeWrapper = node => {
  return _.get(node, "dataSetNodeWrapper.properties.subjects", []);
};

export const getPlannerElementOptions = ({
  fieldObj,
  plannerElements,
  plannerElementNodeSets,
  unitPlanFields,
  grades,
  genericTags,
  objectiveLevelNodes,
  t,
  fieldKey,
  fieldList,
  courseId,
  subjectRecommendedPhaseMap,
  curriculumType,
  isCourseOverViewAndViewMode = false,
  shouldGroupObjectivesBySubject = false,
}) => {
  const dynamicSubject = getPropertyValue({
    fieldObj,
    propertyPath: "dynamicSubject",
  });

  const isDisciplinary = getPropertyValue({
    fieldObj,
    propertyPath: "isDisciplinary",
  });

  const uiType = getPropertyValue({
    fieldObj,
    propertyPath: "uiType",
  });

  const plannerElementConfig =
    getPropertyValue({
      fieldObj,
      propertyPath: "plannerElementObj",
    }) || {};

  const unitType = _.get(
    _.find(unitPlanFields, { uid: "unitType" }),
    "value",
    ""
  );

  const {
    type,
    filteredBy,
    subType,
    isPlannerElementWithoutParent,
    rootNodeLabel,
    parentSubType,
  } = plannerElementConfig;
  const plannerElementSet =
    _.find(plannerElements, {
      type: type,
    }) || {};

  const plannerElementNodeSet =
    _.find(plannerElementNodeSets, {
      type,
    }) || {};

  let plannerElementNodes = !_.isEmpty(plannerElementSet)
    ? _.get(plannerElementSet, "nodes", [])
    : _.map(
        _.get(
          plannerElementNodeSet,
          "plannerElementNodeSetWrapperV2.publishedData.edges",
          []
        ),
        "node"
      );
  if (isCourseOverViewAndViewMode && _.isEmpty(plannerElementNodes)) {
    // use nodes from resolve minimal tree, when plannerElementSet nodes are empty, this case occurs when viewing from courseOverView with updated UI components
    plannerElementNodes = _.get(
      _.find(unitPlanFields, { uid: fieldKey }),
      "resolvedMinimalTree.nodes",
      []
    );
  }
  let options = {};
  let parentNodes = [];
  let nodes = [];
  let value = [];
  switch (type) {
    case "MYP_ATL":
    case "UBD_ATL":
    case "DP_ATL":
    case "UBD_SKILL": {
      parentNodes = _.filter(plannerElementNodes, ({ parent }) => !parent);
      return {
        parentNodes: [
          {
            id: type,
            label: "",
          },
        ],
        plannerElementNodeSetData: {
          id: plannerElementNodeSet.id,
          type,
          parentType: "PLANNER_ELEMENT",
          groupedByType: null,

          filters: {
            grades,
          },
        },
      };
    }

    //It should be grouped by Subject Group category
    case "MYP_OBJECTIVES": {
      let filters = {};
      /*
      Handled for Interdiscplinary Criteria
    */

      if (subType == "INTERDISCIPLINARY_CRITERIA") {
        filters = {
          subTypes: [subType],
        };
        return {
          parentNodes: [
            {
              id: "INTERDISCIPLINARY_CRITERIA",
              label: "Interdisciplinary objectives",
            },
          ],
          plannerElementNodeSetData: {
            type,
            subType,
            parentType: "PLANNER_ELEMENT",
            id: plannerElementNodeSet.id,
            groupedByType: null,
            // The filters are directly consumed by plannerElementDataSet queries to fetch the data
            filters,
          },
        };
      }
      //Handled for Subject Objectives
      else {
        const resolvedValue = _.get(
          _.find(unitPlanFields, { uid: "subjects" }),
          "resolvedMinimalTree.subjects",
          []
        );

        const objectiveLevelsData =
          _.find(unitPlanFields, { uid: "objectiveLevel" }) || {};
        let selectedObjectiveLevels = _.get(objectiveLevelsData, "value", "");
        //filter out the empty objectives
        selectedObjectiveLevels = _.filter(selectedObjectiveLevels, Boolean);
        let courseDetails = {};
        if (courseId) {
          courseDetails = getCourseDetailsFromCache({
            id: courseId,
            courseGradeFilters: { academicYearId: getSelectedAcademicYearId() },
          });
        }

        parentNodes = _.map(resolvedValue, value => {
          let recommendedPhase = "";
          if (!_.isEmpty(courseDetails)) {
            const primaryGrade = _.get(courseDetails, "primaryGrade.id", {});
            const subjectRecommendedPhase = _.get(
              subjectRecommendedPhaseMap,
              value.id,
              []
            );

            recommendedPhase = _.get(
              _.find(subjectRecommendedPhase, { gradeId: primaryGrade }),
              "phaseId",
              ""
            );
          }
          return {
            id: value.id,
            label: value.name,
            recommendedPhase,
            recommendedPhaseLabel: _.find(objectiveLevelNodes, {
              id: recommendedPhase,
            })?.label,
            primaryGrade: _.get(courseDetails, "primaryGrade", {}),
            selectedObjectiveIds: _.reduce(
              selectedObjectiveLevels,
              (result, objectiveId) => {
                const objective = _.find(objectiveLevelNodes, {
                  id: objectiveId,
                });
                const subjects = getSubjectsFromDataSetNodeWrapper(objective);

                if (_.find(subjects, { id: value.id })) {
                  result.push(objectiveId);
                }
                return result;
              },
              []
            ),
          };
        });

        //For Disciplinary Subject Grounding Step, It should be only one lens of that subject group
        if (isDisciplinary) {
          parentNodes = _.filter(
            parentNodes,
            parentNode => parentNode.id == dynamicSubject.id
          );
        }

        // All subjects are our parent nodes
        filters = {
          subjects: _.map(parentNodes, "id"),
        };

        const isMYPObjectivePhasesEnabled = _.isEqual(
          curriculumType,
          CURRICULUM_TYPE_MYP
        )
          ? !ACLStore.can("FeatureFlag:RemoveMYPObjectivesPhase") ||
            ACLStore.can(ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION)
          : true;

        // For MYP objectives, we need to filter out subjects on basis selected objective ids
        let associatedSubjectNodes = parentNodes;
        if (isMYPObjectivePhasesEnabled) {
          const {
            filteredParentNodes,
            subjectWithSelectedObjectives,
          } = _.reduce(
            parentNodes,
            (result, parentNode) => {
              const selectedObjectiveIds = parentNode.selectedObjectiveIds;
              if (!_.isEmpty(selectedObjectiveIds))
                result.subjectWithSelectedObjectives.push(parentNode);
              result.filteredParentNodes.push(...selectedObjectiveIds);
              return result;
            },
            { filteredParentNodes: [], subjectWithSelectedObjectives: [] }
          );
          // filters are used to fetch the data
          filters = {
            ...filters,
            parentNodes: filteredParentNodes,
          };
          associatedSubjectNodes = subjectWithSelectedObjectives;
        }
        const groupedByType = "SUBJECT";

        return {
          parentNodes,
          plannerElementNodeSetData: {
            type,
            groupedByType,
            id: plannerElementNodeSet.id,
            parentType: "PLANNER_ELEMENT",
            filters,
            associatedSubjectNodes,
            ...(shouldGroupObjectivesBySubject
              ? { parentNodes, shouldGroupObjectivesBySubject }
              : {}),
          },
          // selectedObjectiveIds
        };
      }
    }
    //It should be grouped by Subjects
    case "MYP_LEARNING_STANDARD":
    case "UBD_LEARNING_STANDARD":
    case "DP_SUBJECT_STANDARD": {
      parentNodes = _.map(
        _.get(
          _.find(unitPlanFields, { uid: "subjects" }),
          "resolvedMinimalTree.subjects",
          []
        ),
        subject => {
          return {
            id: subject.id,
            label: subject.name,
            isArchived: _.get(subject, "isArchived", false),
          };
        }
      );

      //For Displinary Subject Grounding Step, It should be only one lens of that subject
      if (isDisciplinary) {
        parentNodes = _.filter(
          parentNodes,
          parentNode => parentNode.id == dynamicSubject.id
        );
      }

      // The filters are directly consumed by plannerElementDataSet queries to fetch the data, currently it will be used for v1 modal where we show old standards.
      const filters = {
        associatedParents: [{ ids: _.map(parentNodes, "id"), type: "SUBJECT" }],
      };

      return {
        parentNodes,
        plannerElementNodeSetData: {
          isDisciplinary,
          plannerElementSetId: _.get(
            plannerElementNodeSet,
            "plannerElementSet.id"
          ),
          type,
          parentType: "PLANNER_ELEMENT",
          groupedByType: "SUBJECT",
          filters,
          // grades for getting pre-selected grades which we will pass to the dropdown filter
          // for default selection
          // parentNodes are used for sideBarNodes & data manipulation
          parentNodes,
          grades,
        },
      };
    }
    case "DP_SYLLABUS": {
      const fieldType = _.get(fieldList, `${fieldKey}.type`, "");

      switch (fieldType) {
        case "SelectDropdown": {
          const filteredPlannerElementNodes = _.filter(plannerElementNodes, {
            subType,
          });

          options = _.map(filteredPlannerElementNodes, node => {
            return { ...node, value: node.id };
          });

          return options;
        }
        default: {
          parentNodes = _.map(
            _.get(
              _.find(unitPlanFields, { uid: "subjects" }),
              "resolvedMinimalTree.subjects",
              []
            ),
            subject => {
              return { id: subject.id, label: subject.name };
            }
          );
          let filters = {
            tags: genericTags,
          };

          // The filters are directly consumed by plannerElementDataSet queries to fetch the data
          filters = {
            subjects: _.map(parentNodes, "id"),
          };

          return {
            parentNodes,
            plannerElementNodeSetData: {
              id: plannerElementNodeSet.id,
              type,
              parentType: "PLANNER_ELEMENT",
              groupedByType: "SUBJECT",
              filters,

              // parentNodes are used for sideBarNodes & data manipulation
              parentNodes,
              // Used for fetching subjectVariants
              subject: _.first(parentNodes),
            },
          };
        }
      }
    }

    case "DP_TOK_CONNECTION": {
      return plannerElementNodes;
    }
    case "MYP_KEY_CONCEPT":
      //For Interdisciplinary unit, show all Key Concepts in a single list
      if (unitType == "ibMYPInterdisciplinary") {
        return _.map(plannerElementNodes, node => {
          return { ...node, value: node.id };
        });
      } else {
        /*
        Show two lens - One is subject specific and second is others
      */
        const subjectGroupedValue = _.get(
          _.find(unitPlanFields, { uid: "subjectGroup" }),
          "value",
          []
        );

        const subjectGroupedNode = _.first(
          _.get(
            _.find(unitPlanFields, { uid: "subjectGroup" }),
            "resolvedMinimalTree.subjectGroups",
            []
          )
        );

        const subjectGroupLabel = _.get(subjectGroupedNode, "name", "");

        //Filter out subject specific options

        const subjectSpecificOptions = filteredPlannerElementNodesByProperties({
          plannerElementNodes,
          filerFieldValue: subjectGroupedValue,
          key: "subjectGroups",
        });

        //Filter out other options
        const otherOptions = _.differenceBy(
          plannerElementNodes,
          subjectSpecificOptions,
          "id"
        );

        //Adding only those lens in which options are not empty
        parentNodes = [];
        if (!_.isEmpty(subjectSpecificOptions)) {
          parentNodes.push({
            id: "SUBJECT_SPECIFIC",
            label: t("common:concepts_with_subject_group_label", {
              subjectGroup: subjectGroupLabel,
            }),
            children: _.map(subjectSpecificOptions, node => {
              // To maintain consistency with other fields i.e. making flat structure we will only pass ids in the children &
              // Children Nodes will be pushed seperately
              return node.id;
            }),
          });

          parentNodes.push(
            ..._.map(subjectSpecificOptions, node => {
              return { ...node, value: node.id, parent: "SUBJECT_SPECIFIC" };
            })
          );
        }
        if (!_.isEmpty(otherOptions)) {
          parentNodes.push({
            id: "OTHERS",
            label: t("common:other_subject_group_key_concepts_label"),
            children: _.map(otherOptions, node => {
              return node.id;
            }),
          });

          parentNodes.push(
            ..._.map(otherOptions, node => {
              return { ...node, value: node.id, parent: "OTHERS" };
            })
          );
        }

        return parentNodes;
      }
    case "MYP_RELATED_CONCEPT": {
      parentNodes = _.map(
        _.get(
          _.find(unitPlanFields, { uid: "subjects" }),
          "resolvedMinimalTree.subjects",
          []
        ),
        subject => {
          return {
            id: subject.id,
            label: subject.name,
            isArchived: _.get(subject, "isArchived", false),
          };
        }
      );
      //For Displinary Subject Grounding Step, It should be only one lens of that subject
      if (isDisciplinary) {
        parentNodes = _.filter(
          parentNodes,
          parentNode => parentNode.id == dynamicSubject.id
        );
      }
      const filters = {
        subjects: _.map(parentNodes, "id"),
      };
      return {
        parentNodes,
        plannerElementNodeSetData: {
          type,
          parentType: "PLANNER_ELEMENT",
          id: plannerElementNodeSet.id,
          groupedByType: "SUBJECT",
          // The filters are directly consumed by plannerElementDataSet queries to fetch the data
          filters,
          parentNodes,
        },
      };
    }
    case "MYP_SPECIFIED_CONCEPTS": {
      const dummyRootNode = getDummyRootNode({
        t,
        type,
        rootNodeLabel,
        rootNodes: _.reduce(
          nodes,
          (result, node) => {
            if (node.depth === 1) result.push(node.id);
            return result;
          },
          []
        ),
      });

      // used for default selection of filters
      const subjectGroups = _.get(
        _.find(unitPlanFields, { uid: "subjectGroup" }),
        "value"
      );

      return {
        parentNodes: [dummyRootNode],
        plannerElementNodeSetData: {
          type,
          parentType: "PLANNER_ELEMENT",
          id: plannerElementNodeSet.id,
          subjectGroups,
        },
      };
    }
    case "DP_AIM":
    case "DP_OBJECTIVES":
      /*
      we are creating a union of nodes here beacause if parent is removed based on filters than we can still display the selected
      nodes
      Eg : if we have selected a HL aim and removed HL from the subject level than we wont get the HL nodes in Planner elements
      so if any of the HL node is selected we can fetch it from resolved minimal tree
      */
      nodes = getPlannerElementNodesWithSelectedValues({
        plannerElementNodes,
        unitPlanFields,
        fieldKey,
      });

      return nodes;

    case "DP_CAS_APPROACHES": {
      options = _.map(
        _.filter(plannerElementNodes, node =>
          _.some(node.associatedParents, item =>
            _.isEqual(item?.metadata?.subType, parentSubType)
          )
        ),
        node => {
          return { ...node, value: node.id };
        }
      );
      return options;
    }
    case "DP_CAS_LEARNING_OUTCOMES": {
      options = _.map(plannerElementNodes, node => {
        // attach display name to each LO's
        const displayName = _.get(node, ["metadata", "display_name"], "");
        const updatedLabel = _.includes(node?.label, displayName)
          ? node?.label
          : `${displayName}: ${node?.label}`;

        return { ...node, value: node.id, label: updatedLabel };
      });

      return options;
    }

    default: {
      if (isPlannerElementWithoutParent) {
        /**
         * When ever we want to use the SNS editor where there is no parent associated to the plannerelement node
         * we need to pass a boolean for that planner element isPlannerElementWithoutParent so this will add a dummy
         * root node to the list of nodes
         */
        const plannerElementSet = _.find(plannerElements, {
          type,
        });
        nodes = getPlannerElementNodesWithSelectedValues({
          plannerElementNodes,
          unitPlanFields,
          fieldKey,
        });
        const dummyRootNode = getDummyRootNode({
          t,
          type,
          rootNodeLabel,
          rootNodes: _.reduce(
            nodes,
            (result, node) => {
              if (node.depth === 1) result.push(node.id);
              return result;
            },
            []
          ),
        });
        parentNodes = [dummyRootNode];
        nodes = [dummyRootNode, ...nodes];
        value = _.reduce(
          nodes,
          (result, node) => {
            const associatedParents = [node.parent];
            if (
              _.find(parentNodes, parentNode => {
                return _.includes(associatedParents, parentNode.id);
              })
            ) {
              result.push(node.id);
            }
            return result;
          },
          []
        );
        nodes = _.map(nodes, node => {
          if (_.isEmpty(node.parent))
            return {
              ...node,
              parent: -1,
            };
          return node;
        });
        /*
          description of options for nestedCHecklist
          parent nodes are the nodes which will be at depth 0
          values are the id of the nodes which we are displaying as options
          nodes will store the details of the values
        */
        return {
          parentNodes,
          value,
          nodes,
          dummyRootNode,
          plannerElementNodeSetData: {
            id: plannerElementNodeSet.id,
            type,
            parentType: "PLANNER_ELEMENT",
            groupedByType: type,
          },
        };
      }

      /*
    Handled If you want to filtered nodes in the frontend
    E.g - Focus Areas are filtered by Global Context, Related Key Concepts are filtered by Subject Group
  */
      if (!_.isEmpty(filteredBy)) {
        _.map(filteredBy, filterKey => {
          const filerFieldValue = _.get(
            _.find(unitPlanFields, { uid: filterKey }),
            "value",
            []
          );

          plannerElementNodes = filteredPlannerElementNodesByProperties({
            plannerElementNodes,
            filerFieldValue,
          });
        });
      }

      const isGenericCard = _.isEqual(uiType, "GenericCardList");

      if (_.isEqual(type, "DP_DIFFERENTIATION_APPROACH") && isGenericCard) {
        const fieldValue = _.get(
          _.find(unitPlanFields, { uid: fieldKey }),
          "value",
          []
        );

        return _.map(fieldValue, item => {
          return {
            ...item,
            label: item.label || item.value,
          };
        });
      }

      options = _.map(plannerElementNodes, node => {
        return { ...node, value: node.id };
      });
      return options;
    }
  }
};

const filteredPlannerElementNodesByProperties = ({
  plannerElementNodes,
  filerFieldValue,
  key = "associatedPlannerElementNodes",
}) => {
  return _.filter(plannerElementNodes, node => {
    const ids = _.map(
      _.get(node, "dataSetNodeWrapper.properties." + key, []),
      "id"
    );

    if (!!filerFieldValue && !_.isArray(filerFieldValue)) {
      filerFieldValue = [filerFieldValue];
    }

    return !_.isEmpty(_.intersection(ids, filerFieldValue));
  });
};

const getPlannerElementNodesWithSelectedValues = ({
  plannerElementNodes,
  unitPlanFields,
  fieldKey,
}) => {
  return _.unionBy(
    plannerElementNodes,
    _.get(
      _.find(unitPlanFields, { uid: fieldKey }),
      "resolvedMinimalTree.nodes",
      []
    ),
    "id"
  );
};

const getDummyRootNode = ({ rootNodeLabel, rootNodes, type, t }) => {
  return {
    id: -1,
    name: t(rootNodeLabel),
    depth: 0,
    type,
    children: rootNodes,
    isDummy: true,
  };
};

const getSubjectList = ({
  grades,
  readOnly,
  unitPlanFields,
  learningCourseId,
  academicYearId,
}) => {
  const resolvedValue = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "resolvedMinimalTree",
    ""
  );

  let subjects = [];
  if (!readOnly) {
    //For MYP Fetch Subjects from Course
    //uniquely combined subjects from gradeDetailsQuery
    subjects = _.get(getUniquelyCombinedGradesDetails(grades), "subjects", []);
  }
  if (!_.isEmpty(learningCourseId)) {
    const learningCourseSubjects = getLearningCourseSubjects({
      academicYearId,
      learningCourseId,
    });
    const filteredSubjects = _.intersectionBy(
      subjects,
      learningCourseSubjects,
      "id"
    );
    return _.uniqBy(
      [...filteredSubjects, ..._.get(resolvedValue, "subjects", [])],
      "id"
    );
  }

  return _.uniqBy([...subjects, ..._.get(resolvedValue, "subjects", [])], "id");
};

const getBenchmarksOptions = ({ grades, unitPlanFields }) => {
  const subjects = getSubjectList({ grades, readOnly: true, unitPlanFields });

  // To maintain consistency with other fields we are making plannerElementNodeSetData,
  // subjects are passed to fetch data
  return {
    // parentNodes are used in middlePane to detect if we wanna show disabled state
    parentNodes: subjects,
    plannerElementNodeSetData: {
      type: ELEMENT_TYPE_PYP_LEARNING_STANDARD,
      grades,
      subjects: _.map(subjects, subject => {
        return {
          ...subject,
          label: subject.name,
        };
      }),
      parentNodes: subjects,
    },
  };
};

const getElementQuesOptions = ({ fieldObj, unitPlanFields, uid }) => {
  const fieldOptions = getPropertyValue({
    fieldObj,
    propertyPath: "options",
  });
  const setKey = pypElementSetMapping[uid];
  const resolvedValue = _.get(
    _.find(unitPlanFields, { uid: uid }),
    "resolvedMinimalTree",
    {}
  );

  const nodes = _.get(resolvedValue, setKey, []);

  const selectedElement = _.get(
    _.find(unitPlanFields, { uid: uid }),
    "value",
    ""
  );
  const elements = [];
  _.forEach(selectedElement, (nodeId, index) => {
    const element = _.find(nodes, { id: nodeId });
    if (element) {
      elements.push({
        label: element.label,
        value: element.id,
        subText: element.subText,
      });
    }
  });
  const extraText = "";

  return { ...fieldOptions, nodes: elements, extraText };
};

const getAtlsOptions = ({ grades, plannerElementNodeSets }) => {
  const plannerElementNodeSet =
    _.find(plannerElementNodeSets, {
      type: ELEMENT_TYPE_PYP_ATL,
    }) || {};
  return {
    // parentNodes are used in the middlePanel to show disabled nodes
    parentNodes: [
      {
        id: ELEMENT_TYPE_PYP_ATL,
        label: "",
      },
    ],
    // To maintain consistency with other fields we are making plannerElementNodeSetData,
    // Grades are passed to fetch data
    plannerElementNodeSetData: {
      id: plannerElementNodeSet.id,
      type: ELEMENT_TYPE_PYP_ATL,
      grades,
    },
  };
};

const getFocusAreaOptions = ({
  unitPlanFields,
  readOnly,
  plannerElements,
  plannerElementNodeSets,
  plannerElementConfigType,
  field_listV2,
}) => {
  const themes = getElementOptions({
    fieldKey: "theme",
    readOnly,
    plannerElements,
    plannerElementNodeSets,
    plannerElementConfigType,
    unitPlanFields,
    fieldObj: _.find(field_listV2, { uid: "theme" }),
  });
  const id = _.get(_.find(unitPlanFields, { uid: "theme" }), "id", "");
  const responseFilter = getUnitPlanFieldResponseFilter();

  const theme =
    _.get(_.find(unitPlanFields, { uid: "theme" }), "value", "") ??
    getUnitPlanFieldFromCache({
      fieldId: id,
      responseFilter,
    }).value;

  const focusAreas = _.get(
    _.find(themes, item => _.get(item, "node.id", "") == theme),
    "node.associatedChildren",
    []
  );
  return _.map(focusAreas, obj => {
    return { label: obj.label, value: obj.id, id: obj.id };
  });
};
const getTeacherQuestionsOptions = ({ unitPlanFields }) => {
  const id = _.get(_.find(unitPlanFields, { uid: "lois" }), "id", "");
  const responseFilter = getUnitPlanFieldResponseFilter();

  return _.filter(
    getUnitPlanFieldFromCache({ fieldId: id, responseFilter }).value,
    item => _.trim(item.value) != ""
  );
};

const getElementOptions = ({
  fieldKey,
  readOnly,
  plannerElements,
  plannerElementNodeSets,
  plannerElementConfigType,
  unitPlanFields,
  fieldObj,
}) => {
  const setKey = pypElementSetMapping[fieldKey];
  let combineNodes = [];
  if (!readOnly) {
    const elementSet =
      _.find(plannerElements, { type: plannerElementConfigType }) || {};
    const elementNodeSet =
      _.find(plannerElementNodeSets, { type: plannerElementConfigType }) || {};

    const nodes = !_.isEmpty(elementSet)
      ? _.get(elementSet, "nodes")
      : _.map(
          _.get(
            elementNodeSet,
            "plannerElementNodeSetWrapperV2.publishedData.edges",
            []
          ),
          "node"
        );

    const hasRootNodes = _.has(elementSet, "rootNodes");
    if (hasRootNodes) {
      const rootNodes = _.get(elementSet, "rootNodes", []);

      _.forEach(rootNodes, (nodeId, index) => {
        const element = _.find(nodes, { id: nodeId });
        if (!_.isEmpty(element)) {
          combineNodes.push(element);
        }
      });
    } else {
      combineNodes = [...combineNodes, ...(nodes || [])];
    }
  }

  const resolvedValue = _.get(
    _.find(unitPlanFields, { uid: fieldKey }),
    "resolvedMinimalTree",
    {}
  );

  const resolvedNodes = _.get(resolvedValue, setKey, []);
  combineNodes = _.unionBy([...combineNodes, ...resolvedNodes], "id");

  // combining concepts and action fields with their respective questions fields
  const subFieldConfig = getPropertyValue({
    fieldObj,
    propertyPath: "subField",
  })?.config;

  const elements = [];
  _.forEach(combineNodes, element => {
    if (!_.isEmpty(element)) {
      elements.push({
        node: element,
        label: element.label,
        metadata: element.metadata,
        value: element.id,
        // cases like uid = theme have descriptors, so we need to show them in subText
        subText: element.subText || element.descriptor,
        icon: _.get(element, "iconUrl") || _.get(element, "metadata.icon"),
        id: element.id,
        ...subFieldConfig,
      });
    }
  });

  return elements;
};

export const renderEmptyDatasetComponentDefaultValue = () => {
  return null;
};

export const getElementValueForPortedFilteredByField = ({
  resolvedMinimalTree,
  value,
}) => {
  return generateNestedSelectedData({
    ...getPYPElementSetFromNodes({
      nodes: _.get(resolvedMinimalTree, "nodes", []),
    }),
    value,
  });
};

export const ADD_FIELD_MODAL = "ADD_FIELD_MODAL";
export const TEMPLATE_EDIT_MODE = "TEMPLATE_EDIT_MODE";
export const NESTED_CHECKLIST_PREVIEW = "NESTED_CHECKLIST_PREVIEW";

// Nested checklist variants
export const SINGLE_LEVEL = "SINGLE_LEVEL";
export const MULTI_LEVEL = "MULTI_LEVEL";
